class LayoutService {
  config;
  constructor(config) {
    this.config = config;
  }
  /**
   * Fetch layout configuration from backend API
   * @param layoutType - The layout type identifier (e.g., 'dashboard', 'category', 'topic')
   * @param params - Additional query parameters
   * @returns Layout response with areas
   */
  async fetchLayout(layoutType, params = {}) {
    try {
      const taskParamName = this.config.taskParamName || "task";
      const actionParamName = this.config.actionParamName || "task";
      const queryParams = new URLSearchParams({
        layout: layoutType,
        format: "json",
        ...params
      });
      if (taskParamName !== actionParamName) {
        queryParams.append(taskParamName, "api.proxy");
        queryParams.append(actionParamName, "layouts.getLayout");
      } else {
        queryParams.append(taskParamName, "layouts.getLayout");
      }
      if (this.config.csrfToken) {
        queryParams.append(this.config.csrfToken, "1");
      }
      const separator = this.config.apiBaseUrl.includes("?") ? "&" : "?";
      const url = `${this.config.apiBaseUrl}${separator}${queryParams.toString()}`;
      if (this.config.debug) {
        console.log(`[LayoutService] Fetching layout: ${layoutType}`, url);
      }
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (this.config.debug) {
        console.log(`[LayoutService] Layout response:`, data);
      }
      if (data.success === false) {
        return {
          success: false,
          error: data.message || "Failed to fetch layout"
        };
      }
      return {
        success: true,
        data: {
          template: {
            areas: data.data?.template?.areas || data.template.areas || [],
            layout: data.data?.template?.layout || data.template.layout
          }
        }
      };
    } catch (error) {
      console.error("[LayoutService] Error fetching layout:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred"
      };
    }
  }
  /**
   * Fetch multiple layouts at once
   * @param layoutTypes - Array of layout type identifiers
   * @returns Map of layout type to response
   */
  async fetchMultipleLayouts(layoutTypes) {
    const results = await Promise.all(
      layoutTypes.map(async (layoutType) => ({
        layoutType,
        response: await this.fetchLayout(layoutType)
      }))
    );
    return results.reduce((acc, { layoutType, response }) => {
      acc[layoutType] = response;
      return acc;
    }, {});
  }
  /**
   * Save layout configuration (admin functionality)
   * @param layoutType - The layout type identifier
   * @param areas - Layout areas configuration
   * @returns Success response
   */
  async saveLayout(layoutType, areas) {
    try {
      const taskParamName = this.config.taskParamName || "task";
      const actionParamName = this.config.actionParamName || "task";
      const formData = new FormData();
      if (taskParamName !== actionParamName) {
        formData.append(taskParamName, "api.proxy");
        formData.append(actionParamName, "layouts.saveLayout");
      } else {
        formData.append(taskParamName, "layouts.saveLayout");
      }
      formData.append("layout", layoutType);
      formData.append("areas", JSON.stringify(areas));
      formData.append("format", "json");
      if (this.config.csrfToken) {
        formData.append(this.config.csrfToken, "1");
      }
      const response = await fetch(this.config.apiBaseUrl, {
        method: "POST",
        body: formData,
        credentials: "same-origin"
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return {
        success: data.success || false,
        error: data.message
      };
    } catch (error) {
      console.error("[LayoutService] Error saving layout:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred"
      };
    }
  }
}
const createLayoutService = (config) => {
  return new LayoutService(config);
};
export {
  LayoutService,
  createLayoutService
};
//# sourceMappingURL=layout-service.js.map
